import React from 'react';

interface TabValidationIndicatorProps {
  hasErrors: boolean;
  errorCount?: number;
  isComplete?: boolean;
  className?: string;
}

export const TabValidationIndicator: React.FC<TabValidationIndicatorProps> = ({
  hasErrors,
  errorCount = 0,
  isComplete = false,
  className = ''
}) => {
  if (hasErrors && errorCount > 0) {
    return (
      <span className={`badge bg-danger ms-2 ${className}`} title={`${errorCount} validation error${errorCount > 1 ? 's' : ''}`}>
        {errorCount}
      </span>
    );
  }
  
  if (isComplete) {
    return (
      <span className={`text-success ms-2 ${className}`} title="Section complete">
        ✓
      </span>
    );
  }
  
  return null;
};

/**
 * Hook to calculate validation status for form tabs
 */
export const useTabValidation = (
  validationErrors: Record<string, string | null>, 
  nestedValidationErrors: {
    experiences?: Array<Record<string, string | null>>;
    educations?: Array<Record<string, string | null>>;
  },
  profile: any
) => {
  const getGeneralInfoStatus = () => {
    // Include all address fields that are now required for professional users
    const generalFields = ['first_name', 'last_name', 'phone', 'zip', 'street_address', 'city', 'state'];
    const errors = generalFields.filter(field => validationErrors[field]);
    
    // Also check for any validation errors that start with these field names (e.g., nested errors)
    const additionalErrors = Object.keys(validationErrors).filter(key => 
      generalFields.some(field => key.startsWith(field)) && 
      validationErrors[key] && 
      !generalFields.includes(key)
    );
    
    const totalErrorCount = errors.length + additionalErrors.length;
    
    // Check if all required fields are completed
    const requiredFields = ['first_name', 'last_name', 'phone', 'zip', 'street_address', 'city', 'state'];
    const isComplete = requiredFields.every(field => 
      profile[field] && 
      profile[field].toString().trim() !== ''
    );
    
    return {
      hasErrors: totalErrorCount > 0,
      errorCount: totalErrorCount,
      isComplete: isComplete && totalErrorCount === 0
    };
  };

  const getAvailabilityStatus = () => {
    // Include position types and job types in availability validation
    const availabilityFields = ['hourly_rate', 'maximum_traveling_distance', 'positions', 'availability', 'position_type_ids', 'job_type_ids'];
    const errors = availabilityFields.filter(field => validationErrors[field]);
    
    // Availability is complete if position types and job types are selected
    const hasPositionTypes = profile.position_type_ids && profile.position_type_ids.length > 0;
    const hasJobTypes = profile.job_type_ids && profile.job_type_ids.length > 0;
    const isComplete = hasPositionTypes && hasJobTypes && errors.length === 0;
    
    return {
      hasErrors: errors.length > 0,
      errorCount: errors.length,
      isComplete: isComplete
    };
  };

  const getExperienceStatus = () => {
    // Check for experience or resume validation error
    let errorCount = validationErrors['experience_or_resume'] ? 1 : 0;
    
    // Add nested validation errors for experiences
    if (nestedValidationErrors.experiences) {
      nestedValidationErrors.experiences.forEach(experienceErrors => {
        if (experienceErrors) {
          errorCount += Object.values(experienceErrors).filter(error => error !== null).length;
        }
      });
    }
    
    // Add nested validation errors for educations
    if (nestedValidationErrors.educations) {
      nestedValidationErrors.educations.forEach(educationErrors => {
        if (educationErrors) {
          errorCount += Object.values(educationErrors).filter(error => error !== null).length;
        }
      });
    }
    
    // Experience is complete if there's experience data or a resume
    const hasExperience = profile.experiences && profile.experiences.length > 0;
    const hasResume = !!profile.resume;
    const isComplete = (hasExperience || hasResume) && errorCount === 0;
    
    return {
      hasErrors: errorCount > 0,
      errorCount: errorCount,
      isComplete: isComplete
    };
  };

  const getSkillsStatus = () => {
    // Skills validation - check if any skills are selected
    const hasSkills = profile.skill_ids && profile.skill_ids.length > 0;
    
    return {
      hasErrors: false, // Skills are typically optional
      errorCount: 0,
      isComplete: hasSkills
    };
  };

  const getDocumentsStatus = () => {
    // Documents validation - check for resume or other documents
    const hasResume = profile.resume_file_name && profile.resume_file_name.trim() !== '';
    const hasDocuments = profile.profile_documents && profile.profile_documents.length > 0;

    return {
      hasErrors: false, // Documents are optional
      errorCount: 0,
      isComplete: hasResume || hasDocuments
    };
  };

  return {
    generalInfo: getGeneralInfoStatus(),
    skills: getSkillsStatus(),
    availability: getAvailabilityStatus(),
    experience: getExperienceStatus(),
    documents: getDocumentsStatus()
  };
};
